import {
  ADMIN_NAVIGATION_ACTION_TYPE_VALUES,
  ADMIN_NAVIGATION_REQUIRED_STRING_FIELDS,
  ADMIN_PANEL_OPTIONAL_BOOLEAN_FIELDS,
  ADMIN_PANEL_OPTIONAL_OBJECT_FIELDS,
  ADMIN_PANEL_OPTIONAL_STRING_FIELDS,
  ADMIN_PANEL_REQUIRED_STRING_FIELDS,
} from "./adminRegistryContract";

const SUPPORTED_ACTION_TYPES = new Set(
  ADMIN_NAVIGATION_ACTION_TYPE_VALUES
);

function isObject(value) {
  return (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value)
  );
}

function isNonEmptyString(value) {
  return (
    typeof value === "string" &&
    value.trim().length > 0
  );
}

function createValidationResult() {
  return {
    errors: [],
    warnings: [],
    summary: {
      panelCount: 0,
      navigationActionCount: 0,
      panelActionCount: 0,
      routeActionCount: 0,
      referencedPanelCount: 0,
      orphanPanelCount: 0,
    },
  };
}

function validatePanelRegistry(panelRegistry, result) {
  if (!isObject(panelRegistry)) {
    result.errors.push(
      "Le registre des panneaux doit être un objet."
    );
    return new Set();
  }

  const panelEntries = Object.entries(panelRegistry);
  const keyPrefixes = new Map();
  const panelModes = new Set();

  result.summary.panelCount = panelEntries.length;

  panelEntries.forEach(([panelMode, config]) => {
    panelModes.add(panelMode);

    if (!isNonEmptyString(panelMode)) {
      result.errors.push(
        "Un panneau possède un panelMode vide."
      );
      return;
    }

    if (!isObject(config)) {
      result.errors.push(
        `Le panneau "${panelMode}" doit être configuré avec un objet.`
      );
      return;
    }

    ADMIN_PANEL_REQUIRED_STRING_FIELDS.forEach((field) => {
      if (!isNonEmptyString(config[field])) {
        result.errors.push(
          `Le panneau "${panelMode}" ne possède pas de champ "${field}" valide.`
        );
      }
    });

    if (!config.component) {
      result.errors.push(
        `Le panneau "${panelMode}" ne possède pas de composant.`
      );
    }

    ADMIN_PANEL_OPTIONAL_STRING_FIELDS.forEach((field) => {
      if (
        config[field] !== undefined &&
        !isNonEmptyString(config[field])
      ) {
        result.errors.push(
          `Le champ "${field}" du panneau "${panelMode}" doit être une chaîne non vide.`
        );
      }
    });

    ADMIN_PANEL_OPTIONAL_OBJECT_FIELDS.forEach((field) => {
      if (
        config[field] !== undefined &&
        !isObject(config[field])
      ) {
        result.errors.push(
          `Le champ "${field}" du panneau "${panelMode}" doit être un objet.`
        );
      }
    });

    ADMIN_PANEL_OPTIONAL_BOOLEAN_FIELDS.forEach((field) => {
      if (
        config[field] !== undefined &&
        typeof config[field] !== "boolean"
      ) {
        result.errors.push(
          `Le champ "${field}" du panneau "${panelMode}" doit être booléen.`
        );
      }
    });

    if (isNonEmptyString(config.keyPrefix)) {
      const existingPanelMode = keyPrefixes.get(
        config.keyPrefix
      );

      if (existingPanelMode) {
        result.errors.push(
          `Le keyPrefix "${config.keyPrefix}" est partagé par les panneaux "${existingPanelMode}" et "${panelMode}".`
        );
      } else {
        keyPrefixes.set(config.keyPrefix, panelMode);
      }
    }
  });

  return panelModes;
}

function validateNavigationRegistry(
  navigationRegistry,
  panelModes,
  result
) {
  if (!Array.isArray(navigationRegistry)) {
    result.errors.push(
      "Le registre de navigation doit être un tableau."
    );
    return new Set();
  }

  const navigationIdentities = new Set();
  const referencedPanelModes = new Set();

  result.summary.navigationActionCount =
    navigationRegistry.length;

  navigationRegistry.forEach((entry, index) => {
    const entryLabel = `Action de navigation #${index + 1}`;

    if (!isObject(entry)) {
      result.errors.push(
        `${entryLabel} doit être un objet.`
      );
      return;
    }

    ADMIN_NAVIGATION_REQUIRED_STRING_FIELDS.forEach((field) => {
      if (!isNonEmptyString(entry[field])) {
        result.errors.push(
          `${entryLabel} ne possède pas de champ "${field}" valide.`
        );
      }
    });

    if (
      isNonEmptyString(entry.tab) &&
      isNonEmptyString(entry.title)
    ) {
      const identity = `${entry.tab}::${entry.title}`;

      if (navigationIdentities.has(identity)) {
        result.errors.push(
          `L'action de navigation "${entry.title}" est déclarée plusieurs fois dans l'onglet "${entry.tab}".`
        );
      } else {
        navigationIdentities.add(identity);
      }
    }

    if (!isObject(entry.action)) {
      result.errors.push(
        `${entryLabel} ne possède pas de configuration "action" valide.`
      );
      return;
    }

    const { action } = entry;

    if (!SUPPORTED_ACTION_TYPES.has(action.type)) {
      result.errors.push(
        `${entryLabel} utilise un type d'action inconnu : "${String(action.type)}".`
      );
      return;
    }

    if (action.type === "panel") {
      result.summary.panelActionCount += 1;

      if (!isNonEmptyString(action.panelMode)) {
        result.errors.push(
          `${entryLabel} doit déclarer un "panelMode".`
        );
        return;
      }

      referencedPanelModes.add(action.panelMode);

      if (!panelModes.has(action.panelMode)) {
        result.errors.push(
          `${entryLabel} référence le panneau inconnu "${action.panelMode}".`
        );
      }
    }

    if (action.type === "route") {
      result.summary.routeActionCount += 1;

      if (
        !isNonEmptyString(action.route) ||
        !action.route.startsWith("/")
      ) {
        result.errors.push(
          `${entryLabel} doit déclarer une route absolue valide.`
        );
      }
    }
  });

  return referencedPanelModes;
}

export function validateAdminRegistries({
  navigationRegistry,
  panelRegistry,
} = {}) {
  const result = createValidationResult();

  const panelModes = validatePanelRegistry(
    panelRegistry,
    result
  );

  const referencedPanelModes = validateNavigationRegistry(
    navigationRegistry,
    panelModes,
    result
  );

  const orphanPanelModes = [...panelModes].filter(
    (panelMode) => !referencedPanelModes.has(panelMode)
  );

  orphanPanelModes.forEach((panelMode) => {
    result.warnings.push(
      `Le panneau "${panelMode}" est enregistré mais n'est référencé par aucune action de navigation.`
    );
  });

  result.summary.referencedPanelCount =
    referencedPanelModes.size;

  result.summary.orphanPanelCount =
    orphanPanelModes.length;

  return result;
}

export function formatAdminRegistryValidation(result) {
  const sections = [];

  if (result.errors.length) {
    sections.push(
      [
        "Erreurs:",
        ...result.errors.map((error) => `- ${error}`),
      ].join("\n")
    );
  }

  if (result.warnings.length) {
    sections.push(
      [
        "Avertissements:",
        ...result.warnings.map(
          (warning) => `- ${warning}`
        ),
      ].join("\n")
    );
  }

  sections.push(
    [
      "Résumé:",
      `- panneaux: ${result.summary.panelCount}`,
      `- actions de navigation: ${result.summary.navigationActionCount}`,
      `- actions panel: ${result.summary.panelActionCount}`,
      `- actions route: ${result.summary.routeActionCount}`,
      `- panneaux référencés: ${result.summary.referencedPanelCount}`,
      `- panneaux orphelins: ${result.summary.orphanPanelCount}`,
    ].join("\n")
  );

  return sections.join("\n\n");
}

export function assertAdminRegistryIntegrity(registries) {
  const result = validateAdminRegistries(registries);

  if (result.errors.length) {
    throw new Error(
      [
        "Q360 Admin Registry validation failed.",
        formatAdminRegistryValidation(result),
      ].join("\n\n")
    );
  }

  return result;
}
